import { useTheme } from '@mui/styles';
import { useRONGetOrder } from 'hooks/useRONGetOrder';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { normalizeApiError } from 'utils/api-error';
import { capitalize } from 'utils/helpers';

import { generateLenderCreditsColumns, tableRowComponent } from './services';
import { useUserInformationContext } from 'contexts/userContext';

const useRonInviteScreen = () => {
  const { t } = useTranslation();
  const [tokensRon, setTokensRon] = useState<tableRowComponent[]>([]);
  const { id: orderId } = useParams<{ id: string }>();
  const [scheduleModal, setScheduleModal] = useState(false);
  const theme = useTheme();
  const showAlert = useAlert();
  const { currentUser } = useUserInformationContext();

  const getOrder = useRONGetOrder(orderId!, {
    enabled: !!orderId,
    refetchOnWindowFocus: false,
  });

  const [previousQuery, setPreviousQuery] = useState<{ data: typeof getOrder.data; success: boolean }>({ data: undefined, success: false });
  if (previousQuery.data !== getOrder.data || previousQuery.success !== getOrder.isSuccess) {
    setPreviousQuery({ data: getOrder.data, success: getOrder.isSuccess });
    if (getOrder.isSuccess && getOrder.data) {
      const { tokens, parties } = getOrder.data.getOrder;
      if (tokens) {
        setTokensRon(
          tokens.map((token) => {
            return {
              ...token,
              party_info: parties.find((party) => party._id === token.party_id),
            };
          })
        );
      }
    }
  }

  const handleCopyUrl = useCallback(
    (url: string) => {
      if (url) {
        navigator.clipboard
          .writeText(url)
          .then(() => {
            showAlert(capitalize(t('documents:link-copied')), 'success');
          })
          .catch((error) => {
            showAlert(normalizeApiError(error), 'error');
          });
      }
    },
    [showAlert, t]
  );

  const columns = useMemo(
    () => generateLenderCreditsColumns(t, theme, handleCopyUrl),
    [handleCopyUrl, t, theme]
  );

  const isUserInvolved =
    getOrder.data?.getOrder.parties.some((party) => party.name === currentUser?.name) ?? false;
  return {
    t,
    columns,
    onCopyUrl: handleCopyUrl,
    tokensRon,
    scheduleModal,
    setScheduleModal,
    loadingOrder: getOrder.isLoading,
    setTokensRon,
    graphOrder: getOrder.data?.getOrder,
    isUserInvolved,
  };
};

export default useRonInviteScreen;
